'use client';

import type { ReactNode } from 'react';
import { BasePathProvider } from '../navigation/base-path';
import { GreenWave } from './green-wave';
import { WaveHero } from './wave-hero';

/**
 * Canonical mobile workspace shell shared between the Telegram miniapp
 * and the web app's `/app` routes on mobile viewports. Owns:
 *   - wave/hero background (GreenWave vs WaveHero)
 *   - header visibility and user data fetch
 *   - page-type CSS modifier classes (.main-layout, .wave-hero-layout, etc)
 *
 * Platform-specific pieces (generator bar, back-button bindings, Telegram
 * native viewport adjustments) live outside this component — platforms
 * pass their own generator bar via the `generatorBar` slot, and install
 * any platform listeners in their own providers.
 */
interface MobileWorkspaceLayoutProps {
  pathname: string;
  /** Prefix stripped from pathname before route classification. `/` for miniapp, `/app` for web. */
  basePath?: string;
  children: ReactNode;
  /**
   * Header slot. Each platform passes its own connected Header (wraps
   * MobileWorkspaceHeader with the platform's user fetch). The layout decides
   * whether to render it based on the current route type.
   */
  header?: ReactNode;
  /** Platform-specific generator bar (miniapp GeneratorBar, future WebWorkspaceGeneratorBar, or null). */
  generatorBar?: ReactNode;
}

function normalizePath(pathname: string, basePath: string): string {
  if (basePath === '/' || basePath === '') return pathname || '/';
  if (!pathname.startsWith(basePath)) return pathname;
  const rest = pathname.slice(basePath.length);
  if (!rest) return '/';
  return rest.startsWith('/') ? rest : `/${rest}`;
}

export function MobileWorkspaceLayout({
  pathname,
  basePath = '/',
  children,
  header,
  generatorBar,
}: MobileWorkspaceLayoutProps) {
  const path = normalizePath(pathname, basePath);

  const isHome = path === '/';
  const isProfile = path === '/profile';
  const isTrendsPage = path === '/trends';
  const usesWaveHero = isHome || isProfile || isTrendsPage;
  const isDetailPage =
    (path.startsWith('/history/') && path !== '/history') ||
    (path.startsWith('/trends/') && path !== '/trends') ||
    // Both /ideas/* (miniapp keeps the legacy path) and /feed/* (web,
    // renamed) need to be classified as detail pages — without this
    // the global header + generator dock float over the screen and
    // hide its CTAs.
    (path.startsWith('/feed/') && path !== '/feed') ||
    (path.startsWith('/ideas/') && path !== '/ideas');
  const isChatPage = path.startsWith('/chat/');
  const isTopUp = path === '/topup';
  const isCredits = path === '/credits';
  const isCreditsHistory = path === '/credits/history';

  const showHeader =
    !isProfile &&
    !isTrendsPage &&
    !isDetailPage &&
    !isTopUp &&
    !isCredits &&
    !isCreditsHistory &&
    !isChatPage;

  const showWave =
    !isDetailPage && !isTopUp && !isCredits && !isCreditsHistory && !isChatPage;

  const showGeneratorBar =
    !isDetailPage && !isTopUp && !isTrendsPage && !isCredits && !isCreditsHistory;

  const layoutClasses = [
    'main-layout',
    'relative flex min-h-screen max-w-full flex-col overflow-x-hidden bg-background',
    usesWaveHero ? 'wave-hero-layout' : '',
    isProfile ? 'profile-layout' : '',
    isTrendsPage ? 'trends-layout' : '',
    isDetailPage || isTopUp || isCredits || isCreditsHistory || isChatPage ? 'detail-layout' : '',
    isTopUp ? 'topup-layout' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const normalizedBase = basePath === '/' ? '' : basePath;

  return (
    <BasePathProvider value={normalizedBase}>
      <div className={layoutClasses}>
        {showWave && (
          <div className="pointer-events-none absolute inset-0 z-0 overflow-visible">
            {usesWaveHero ? <WaveHero /> : <GreenWave />}
          </div>
        )}

        {showHeader && header}

        <main className="relative z-[5] flex min-h-0 flex-1 flex-col">{children}</main>

        {showGeneratorBar && generatorBar}
      </div>
    </BasePathProvider>
  );
}
